import { sidebarClientsKey } from "@baton/core";
import { useI18n } from "@baton/i18n";
import { useState } from "react";
import { Globe } from "@baton/icons";
import { flagOf } from "../../accounts/CountryFlag.tsx";
import { placeName } from "../../accounts/location.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import type { ClientHeading } from "./clients.ts";
import { GlideList } from "./GlideList.tsx";
import { HeadingGroup } from "./HeadingGroup.tsx";
import { SIDEBAR_PILL, SidebarItem } from "./Sidebar.tsx";

/**
 * 020 FR-020-173, `contracts/screens.md` § 020 Accounts and pipeline, Projects by client: the
 * Projects branch grouped by client (`clients.ts`). Each heading is a disclosure over its projects,
 * open until it is collapsed, with the count of its projects at its end; which headings are collapsed
 * is kept per workspace in local storage beside the open branches (FR-015-110), so a reload keeps
 * them. A project under a client that spans more than one place carries its account's country as
 * its flag, or the globe for an account located by a region alone (020 FR-020-262), named in full for
 * a screen reader and in the row's title. Every visible project is
 * listed, because a heading that collapses is what keeps the branch short.
 */
export interface ClientHeadingsProps {
  readonly ws: string;
  readonly headings: readonly ClientHeading[];
  readonly pathname: string;
  readonly onNavigate?: () => void;
}

export function ClientHeadings({ ws, headings, pathname, onNavigate }: ClientHeadingsProps) {
  const i18n = useI18n();
  const { t } = i18n;
  const runtime = useRuntime();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => runtime.storage.get(sidebarClientsKey, ws) ?? {});
  const toggle = (id: string) => {
    setCollapsed((current) => {
      const updated = { ...current, [id]: !(current[id] ?? false) };
      runtime.storage.set(sidebarClientsKey, updated, ws);
      return updated;
    });
  };
  const navigateProps = onNavigate === undefined ? {} : { onNavigate };
  return (
    <div className="b-sidebar__clients">
      {headings.map((heading) => {
        const name = heading.kind === "unnamed" ? t("nav.clients.unnamed") : heading.kind === "internal" ? t("nav.clients.internal") : (heading.name ?? "");
        return (
          <HeadingGroup key={heading.id} name={name} count={heading.projects.length} countLabel={t("nav.clients.count", { count: heading.projects.length })} open={!(collapsed[heading.id] ?? false)} onToggle={() => { toggle(heading.id); }}>
            <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL}>
              {heading.projects.map(({ project, place }) => {
                const base = `/${ws}/projects/${encodeURIComponent(project.identifier)}`;
                return <SidebarItem key={project.id} to={`${base}/tasks`} label={project.name} current={pathname === base || pathname.startsWith(`${base}/`)} nested {...(place === null ? {} : { tag: { text: place.kind === "country" ? flagOf(place.code) : <Globe />, label: placeName(i18n, place) } })} {...navigateProps} />;
              })}
            </GlideList>
          </HeadingGroup>
        );
      })}
    </div>
  );
}
